import { getUrlWithSchema } from "@shared/common/utils/url";
import type { ExternalSiteRule } from "../../types";
import { SHOPBY_PRO_PAGE, SITE_TYPE } from "./const";

const INVALID_SITE_INFO = "INVALID";

async function getSiteEnvironment(host: string) {
  const url = `${host}environment.json`;
  try {
    const ret = await fetch(url);
    const data = await ret.json();

    if (!ret.ok) {
      return null;
    }
    return data;
  } catch (err) {
    throw new Error(`${url} fetch error! ${err}`);
  }
}

async function getSiteTitle(host: string) {
  let title = null;

  try {
    const { clientId } = await getSiteEnvironment(host);

    if (clientId) {
      const headers = {
        "Content-Type": "application/json; charset=utf-8",
        clientId,
        platform: "PC",
        Version: "1.0",
      };
      const url = "https://shop-api.e-ncp.com/malls";

      const ret = await fetch(url, { headers });
      const data = await ret.json();

      if (ret.ok) {
        title = data.mall.mallName;
      }
    }
  } catch (err) {
    return null;
  }

  return title;
}

const shopbyPro: ExternalSiteRule = {
  type: SITE_TYPE,

  getInfo: async ({ html, responseUrl }) => {
    if (!html.innerHTML.includes("/js/core/shopby.js")) {
      return INVALID_SITE_INFO;
    }

    // 사용자의 사이트에서 자바스크립트 동작 이후 타이틀이 변경되는 케이스에 대한 처리
    const title = await getSiteTitle(responseUrl);

    return {
      siteType: SITE_TYPE,
      url: responseUrl,
      title,
      siteInfo: {
        type: SITE_TYPE,
      },
    };
  },

  pageRules: [
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.LOGIN.target,
      label: "로그인 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.LOGIN.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.SIGNUP_INTRO.target,
      label: "가입하기 페이지 - 인트로",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${
          SHOPBY_PRO_PAGE.SIGNUP_INTRO.url
        }`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.SIGNUP_AGREEMENT.target,
      label: "가입하기 페이지 - 약관동의",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${
          SHOPBY_PRO_PAGE.SIGNUP_AGREEMENT.url
        }`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.SIGNUP_MAIN.target,
      label: "가입하기 페이지 - 메인",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.SIGNUP_MAIN.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.MY_PAGE.target,
      label: "마이 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.MY_PAGE.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.FIND_ID.target,
      label: "아이디 찾기 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.FIND_ID.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.FIND_PASSWORD.target,
      label: "비밀번호 찾기 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${
          SHOPBY_PRO_PAGE.FIND_PASSWORD.url
        }`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.CART.target,
      label: "장바구니 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.CART.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.POLICY.target,
      label: "이용약관 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.POLICY.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.PRIVACY.target,
      label: "개인정보처리방침 페이지",
      getExactUrl({ siteUrl }) {
        return `${getUrlWithSchema(siteUrl)}${SHOPBY_PRO_PAGE.PRIVACY.url}`;
      },
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.PRODUCT_LIST.target,
      label: "상품 목록 페이지",
      getExactUrl({ siteUrl, params }) {
        if (params?.productSlug) {
          return `${getUrlWithSchema(siteUrl)}${
            SHOPBY_PRO_PAGE.PRODUCT_LIST.url
          }?${params.productSlug}`;
        }
        return `${getUrlWithSchema(siteUrl)}${
          SHOPBY_PRO_PAGE.PRODUCT_LIST.url
        }?...`;
      },
      allowDuplicate: true,
      params: [
        {
          key: "productSlug",
          formType: "TEXT",
          placeholder: "/하위경로를 입력해주세요",
        },
      ],
    },
    {
      siteType: SITE_TYPE,
      targetValue: SHOPBY_PRO_PAGE.PRODUCT_DETAIL.target,
      label: "상품 상세 페이지",
      getExactUrl({ siteUrl, params }) {
        if (params?.productSlug) {
          return `${getUrlWithSchema(siteUrl)}${
            SHOPBY_PRO_PAGE.PRODUCT_DETAIL.url
          }?${params.productSlug}`;
        }
        return `${getUrlWithSchema(siteUrl)}${
          SHOPBY_PRO_PAGE.PRODUCT_DETAIL.url
        }?...`;
      },
      allowDuplicate: true,
      params: [
        {
          key: "productSlug",
          formType: "TEXT",
          placeholder: "/하위경로를 입력해주세요",
        },
      ],
    },
  ],
};

export default shopbyPro;
